// config.ts — written by generateOpenAPITsClient alongside each generated package.
// Not auto-generated by @hey-api/openapi-ts; hand-maintained in lib/openapi_templates/hey-api/.
//
// Public API of this package for wiring auth state:
//
//   import { getBaseURL, setBaseURL,
//            getAccessToken, setAccessToken,
//            getRefreshToken, setRefreshToken,
//            getCurrentUser, setCurrentUser,
//            logout,
//            apiClient } from '@next-qr/openapi-client-<service>/config';
//
// Each service's tokens/baseURL/user live under its own isolated storage scope
// (`@next-qr:{service}:*`). Nothing is shared across services — the UI of each
// mini-app owns its own login flow and persists the tokens via `setAccessToken`
// / `setRefreshToken` below.

import { client } from './client.gen';
import {
  configureAuthInterceptors,
  getStorage,
  STORAGE_KEYS,
  DEFAULT_API_BASE_URL,
  readStoredBaseURL,
  readStoredToken,
  readStoredRefreshToken,
  readStoredUser,
  clearAuthData,
} from './auth-setup';

// ── Module-level cache ───────────────────────────────────────────────────────
// Hydrated async at module init. Sync getters read from this cache so callers
// (e.g. `useLiveQuery`) that need synchronous values during render work even
// before storage has been read.

let _cachedBaseURL:     string | null = null;
let _cachedToken:       string | null = null;
let _cachedRefresh:     string | null = null;
let _cachedUser:        Record<string, unknown> | null = null;

const _storage = getStorage();

// Kick off hydration immediately — setters also keep the cache in sync.
readStoredBaseURL(_storage)
  .then((url) => {
    if (!_cachedBaseURL) {
      _cachedBaseURL = url;
      if (url) client.setConfig({ baseURL: url });
    }
  })
  .catch(() => { /* ignore */ });

readStoredToken(_storage).then((t) => { _cachedToken = t; }).catch(() => { /* ignore */ });
readStoredRefreshToken(_storage).then((t) => { _cachedRefresh = t; }).catch(() => { /* ignore */ });
readStoredUser(_storage).then((u) => { _cachedUser = u; }).catch(() => { /* ignore */ });

// Attach the request+refresh interceptors once.
configureAuthInterceptors(client, { storage: _storage });

// ── Public API ───────────────────────────────────────────────────────────────

/** Current base URL. Returns the in-memory value (hydrated at init, kept in sync by setBaseURL). */
export function getBaseURL(): string {
  return _cachedBaseURL ?? client.getConfig().baseURL ?? DEFAULT_API_BASE_URL;
}

/** Persist a new base URL to storage and apply it to the client. */
export async function setBaseURL(url: string): Promise<void> {
  _cachedBaseURL = url;
  client.setConfig({ baseURL: url });
  await _storage.setItem(STORAGE_KEYS.baseUrl, url);
}

/** Current access token (sync, from cache). Returns null if not logged in. */
export function getAccessToken(): string | null {
  return _cachedToken;
}

/** Persist a new access token to storage. Pass `null` to remove. */
export async function setAccessToken(token: string | null): Promise<void> {
  _cachedToken = token;
  if (token == null) await _storage.removeItem(STORAGE_KEYS.token);
  else await _storage.setItem(STORAGE_KEYS.token, token);
}

/** Current refresh token (sync, from cache). */
export function getRefreshToken(): string | null {
  return _cachedRefresh;
}

/** Persist a new refresh token to storage. Pass `null` to remove. */
export async function setRefreshToken(token: string | null): Promise<void> {
  _cachedRefresh = token;
  if (token == null) await _storage.removeItem(STORAGE_KEYS.refreshToken);
  else await _storage.setItem(STORAGE_KEYS.refreshToken, token);
}

/** Current user object (sync, from cache). */
export function getCurrentUser<T extends Record<string, unknown> = Record<string, unknown>>(): T | null {
  return (_cachedUser as T | null);
}

/** Persist a new user object to storage. Pass `null` to remove. */
export async function setCurrentUser(user: Record<string, unknown> | null): Promise<void> {
  _cachedUser = user;
  if (user == null) await _storage.removeItem(STORAGE_KEYS.user);
  else await _storage.setItem(STORAGE_KEYS.user, JSON.stringify(user));
}

/** Clears access token, refresh token, and user from storage and cache. */
export async function logout(): Promise<void> {
  _cachedToken = null;
  _cachedRefresh = null;
  _cachedUser = null;
  await clearAuthData(_storage);
}

/**
 * Pre-built client stub for `useLiveQuery`'s `client` option.
 * Exposes the sync getters so hooks can read the baseURL and token during render.
 *
 * @example
 * import { apiClient } from '@next-qr/openapi-client-chat';
 * useLiveQuery({ queryKey: [...], queryFn: ..., client: apiClient });
 */
export const apiClient = {
  getBaseURL,
  setBaseURL,
  getAccessToken,
  setAccessToken,
  getRefreshToken,
  setRefreshToken,
  getCurrentUser,
  setCurrentUser,
  logout,
};
